import React from "react";
import { format } from "date-fns";
import { DATE_FNS_LONG_DATE_FORMAT } from "../../../shared/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserClock,
    faCopy,
    faImage,
    faListOl,
    faStickyNote,
    faTruck,
    faBoxOpen,
    faDrumstickBite,
    faBoxesStacked,
} from "@fortawesome/free-solid-svg-icons";

import NoteButton from "../../../components/notes/note-button";
import ThreeWayCheckbox from "../../../components/form-inputs/custom/three-way-checkbox";

import { DueDateFilterButton } from "../../../components/table/components/dueDateFilterButton";
import { convertToCT } from "components/task-list/lib/utils";
import { Checkbox, Select } from "../../../components/form-inputs/plain-inputs";
import { WithIcon, WithTooltip } from "../../../shared/table-helpers";
import { inputFilter, TaskType } from "../../../components/task-list/lib/constants";
import { SHIPMENT_FILTER_TYPES, SHIPMENT_TYPE_MAP } from "components/shipments/lib/constants";
import { TaskStatus } from "shared/constants/task-status";
import { basicOptions } from "../../../components/task-list/lib/constants";

export const DEFAULT_SETTINGS = {
    page: 1,
    pageSize: 20,
    sorted: [],
    filtered: [],
};

export const PRIORITY_LEVELS = [
    { text: "1", value: "1" },
    { text: "2", value: "2" },
    { text: "3", value: "3" },
    { text: "4", value: "4" },
    { text: "5", value: "5" },
    { text: "6", value: "6" },
    { text: "7", value: "7" },
    { text: "8", value: "8" },
    { text: "9", value: "9" },
    { text: "10", value: "10" },
];

export const buildColumns = (
    updateSelectedUpc,
    setAllUpcs,
    idList,
    locations,
    assignments,
    statuses,
    userAssignments,
    updateTeamFilter,
    taskType,
    isWorkflow
) => {
    const SOURCE_PAGE = "assignment";
    const statusMap = statuses.reduce((obj, i) => {
        obj[i.value] = i.text;

        return obj;
    }, {});

    return [
        {
            Header: WithIcon("Priority", faListOl),
            accessor: "priority",
            Cell: (row) => {
                return row.value && row.value !== 255 ? row.value : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={filter ? filter.value : ""}
                        options={[{ text: " ", value: "" }, ...PRIORITY_LEVELS, { text: "≠", value: "null" }]}
                        excludeSelect={true}
                    />
                );
            },
            width: 40,
            style: { textAlign: "center" },
        },
        {
            Header: (props) => {
                const value = idList.length === props.data.length;
                const allProducts = props.data.map((p) => p._original);

                return (
                    <Checkbox
                        name="selected-state"
                        value={value}
                        onChange={() => setAllUpcs(value ? [] : allProducts)}
                    />
                );
            },
            accessor: "context.key",
            filterable: false,
            sortable: false,
            Cell: (row) => {
                return (
                    <Checkbox
                        name={`select_${row.original.key}`}
                        value={idList.includes(row.original.key)}
                        onChange={(e) => {
                            updateSelectedUpc(row.original, e.currentTarget.value);
                        }}
                    />
                );
            },
            width: 65,
            className: "txt-center",
        },
        {
            Header: WithIcon("ClientApproval", faUserClock),
            accessor: "context.imagesNeedClientApproval",
            Cell: (row) => {
                return row.value ? <FontAwesomeIcon icon={faUserClock} /> : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <ThreeWayCheckbox
                        onChange={onChange}
                        value={filter ? filter.value : ""}
                    />
                );
            },
            width: 40,
        },
        {
            Header: WithIcon("Rush", faTruck),
            accessor: "isRush",
            Cell: (row) => {
                return row.value ? <FontAwesomeIcon icon={faTruck} /> : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <ThreeWayCheckbox
                        onChange={onChange}
                        value={filter ? filter.value : ""}
                    />
                );
            },
            width: 40,
        },
        {
            Header: WithIcon("Premium Editing", faImage),
            accessor: "context.hasPremiumEditing",
            Cell: (row) => {
                return row.value ? <FontAwesomeIcon icon={faImage} /> : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <ThreeWayCheckbox
                        onChange={onChange}
                        value={filter ? filter.value : ""}
                    />
                );
            },
            width: 40,
        },
        {
            Header: WithIcon("Foodservice", faDrumstickBite),
            accessor: "context.isFoodService",
            Cell: (row) => {
                return row.value ? <FontAwesomeIcon icon={faDrumstickBite} /> : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <ThreeWayCheckbox
                        onChange={onChange}
                        value={filter ? filter.value : ""}
                    />
                );
            },
            width: 40,
        },
        {
            Header: WithIcon("Supplier Coding Attributes", faBoxesStacked),
            accessor: "context.hasSupplierCodingAttributes",
            Cell: (row) => {
                return row.value ? <FontAwesomeIcon icon={faBoxesStacked} /> : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <ThreeWayCheckbox
                        onChange={onChange}
                        value={filter ? filter.value : ""}
                    />
                );
            },
            width: 40,
            show: taskType === TaskType.CODING,
        },
        {
            Header: WithIcon("Duplicate Match", faCopy),
            accessor: "context.matchedProductTaskContextKey",
            Cell: (row) => {
                return row.value ? <FontAwesomeIcon icon={faCopy} /> : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <ThreeWayCheckbox
                        onChange={onChange}
                        value={filter ? filter.value : ""}
                    />
                );
            },
            width: 40,
        },
        {
            Header: WithIcon("Shipment Type", faBoxOpen),
            accessor: "context.shipmentType",
            Cell: (row) => {
                const shipmentType = SHIPMENT_TYPE_MAP[row.original.context.shipmentType].label;

                return WithTooltip(shipmentType);
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || "all"}
                        id="select-type-filter"
                        options={SHIPMENT_FILTER_TYPES}
                        excludeSelect={true}
                    />
                );
            },
        },
        {
            Header: WithTooltip("Due Date (Central Time)"),
            filterable: true,
            accessor: "due",
            Filter: ({ filter, onChange }) => {
                return (
                    <DueDateFilterButton
                        filterProp={filter}
                        onChangeProp={onChange}
                    />
                );
            },
            Cell: (row) => {
                const dateString = row.value ? convertToCT(row.value) : "";

                return WithTooltip(dateString);
            },
        },
        {
            Header: WithTooltip("Client"),
            accessor: "client",
            Filter: !!isWorkflow ? inputFilter : null,
            show: !!isWorkflow,
        },
        {
            Header: WithTooltip("Shipment Due Date"),
            filterable: true,
            accessor: "shipmentDueDate",
            Filter: ({ filter, onChange }) => {
                return (
                    <DueDateFilterButton
                        filterProp={filter}
                        onChangeProp={onChange}
                    />
                );
            },
            Cell: (row) => {
                const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

                return WithTooltip(dateString);
            },
            show: !isWorkflow,
        },
        {
            Header: WithTooltip("CES Client"),
            accessor: "cxHClient",
            Filter: inputFilter,
        },
        {
            Header: WithTooltip("Bin ID"),
            accessor: "context.binTrackingNumber",
            Filter: inputFilter,
        },
        {
            Header: WithTooltip("UPC"),
            accessor: "context.upc",
            Filter: inputFilter,
            Cell: (row) => {
                const fullUpc = row.original.context.productModifier ? `${row.value}-${row.original.context.productModifier}` : row.value;

                return WithTooltip(fullUpc);
            },
        },
        {
            Header: WithTooltip("Task Description"),
            accessor: "description",
            Filter: inputFilter,
        },
        {
            Header: WithTooltip("Location"),
            accessor: "locationInfo.location",
            Cell: (row) => {
                return WithTooltip(row.original.locationInfo.location);
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={filter ? filter.value : ""}
                        options={locations}
                    />
                );
            },
        },
        {
            Header: WithTooltip("Status"),
            //filterable: false,
            accessor: "state",
            Cell: (row) => {
                const state = row.value === TaskStatus.TO_DO ? "Open" : statusMap[row.value] || row.value;

                return WithTooltip(state);
            },
            filterMethod: (filter, row) => {
                if (filter.value === "all") {
                    return true;
                }

                return row[filter.id] === filter.value;
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || ""}
                        excludeSelect={true}
                        id="select-all-filter"
                        options={statuses}
                    />
                );
            },
        },
        {
            Header: WithTooltip("Team Assignment"),
            accessor: "assignmentGroupKey",
            filterable: true,
            Cell: (row) => {
                const assignment = assignments.find((a) => a.value === row.value);
                const assignmentText = assignment && assignment.text ? assignment.text : "";

                return <span>{assignmentText}</span>;
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => {
                            onChange(event.target.value);

                            return updateTeamFilter(event.target.value);
                        }}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || ""}
                        excludeSelect={true}
                        id="select-all-filter"
                        options={[...basicOptions, ...assignments]}
                    />
                );
            },
        },
        {
            Header: WithTooltip("User Assignment"),
            accessor: "assignedUserInfo",
            id: "fullName",
            filterable: true,
            Cell: (row) => {
                const displayName = row.value ? row.value.fullName : "";

                return <span>{displayName}</span>;
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || ""}
                        excludeSelect={true}
                        id="select-all-filter"
                        options={[...basicOptions, ...userAssignments]}
                    />
                );
            },
        },
        {
            Header: WithIcon("Notes", faStickyNote),
            accessor: "note",
            sortable: false,
            Cell: (row) => {
                const fullUpc = row.original.context.productModifier
                    ? `${row.original.context.upc}-${row.original.context.productModifier}`
                    : row.original.context.upc;

                return (
                    <NoteButton
                        upc={fullUpc}
                        shipmentKey={row.original.context.shipmentKey}
                        shipmentProductKey={row.original.context.shipmentProductKey}
                        noteType="Product"
                        cxHClientCode={row.original.context.cxHClientCode}
                        hasNotes={row.original.hasComments}
                        sourcePage={SOURCE_PAGE}
                    />
                );
            },
            filterable: false,
            width: 40,
        },
    ];
};

export const buildProfilingColumns = (
    updateSelectedUpc,
    setAllUpcs,
    idList,
    locations,
    assignments,
    statuses,
    userAssignments,
    updateTeamFilter,
    taskType,
    locationDataSources
) => {
    const SOURCE_PAGE = "assignment";
    const statusMap = statuses.reduce((obj, i) => {
        obj[i.value] = i.text;

        return obj;
    }, {});

    return [
        {
            Header: WithIcon("Priority", faListOl),
            accessor: "profilingTasksAssignmentPriority",
            Cell: (row) => {
                return row.value && row.value !== 255 ? row.value : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={filter ? filter.value : ""}
                        options={[{ text: " ", value: "" }, ...PRIORITY_LEVELS, { text: "≠", value: "null" }]}
                        excludeSelect={true}
                    />
                );
            },
            width: 40,
            style: { textAlign: "center" },
        },
        {
            Header: (props) => {
                const value = idList.length === props.data.length;
                const allProducts = props.data.map((p) => p._original);

                return (
                    <Checkbox
                        name="selected-state"
                        value={value}
                        onChange={() => setAllUpcs(value ? [] : allProducts)}
                    />
                );
            },
            accessor: "context.key",
            filterable: false,
            sortable: false,
            Cell: (row) => {
                return (
                    <Checkbox
                        name={`select_${row.original.key}`}
                        value={idList.includes(row.original.key)}
                        onChange={(e) => {
                            updateSelectedUpc(row.original, e.currentTarget.value);
                        }}
                    />
                );
            },
            width: 65,
            className: "txt-center",
        },
        {
            Header: WithTooltip("# of UPCs"),
            filterable: false,
            sortable: false,
            accessor: "upcs",
            Cell: (row) => {
                return WithTooltip(row.value.length);
            },
        },
        {
            Header: WithIcon("Rush", faTruck),
            accessor: "isRush",
            Cell: (row) => {
                return row.value ? <FontAwesomeIcon icon={faTruck} /> : "";
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <ThreeWayCheckbox
                        onChange={onChange}
                        value={filter ? filter.value : ""}
                    />
                );
            },
            width: 40,
        },
        {
            Header: WithTooltip("Due Date"),
            accessor: "schedulingDueDateTime",
            Filter: ({ filter, onChange }) => {
                return (
                    <DueDateFilterButton
                        filterProp={filter}
                        onChangeProp={onChange}
                    />
                );
            },
            Cell: (row) => {
                const dateString = row.value ? convertToCT(row.value) : "";

                return WithTooltip(dateString);
            },
        },
        {
            Header: WithTooltip("Shipment Due Date"),
            accessor: "dueOn",
            Filter: ({ filter, onChange }) => {
                return (
                    <DueDateFilterButton
                        filterProp={filter}
                        onChangeProp={onChange}
                    />
                );
            },
            Cell: (row) => {
                const dateString = row.value ? convertToCT(row.value) : "";

                return WithTooltip(dateString);
            },
        },
        {
            Header: WithTooltip("Source"),
            accessor: "locationDataSource",
            Cell: (row) => {
                return WithTooltip(row.value);
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        excludeSelect={false}
                        value={(filter && filter.value) || ""}
                        options={locationDataSources}
                    />
                );
            },
        },
        {
            Header: WithTooltip("CES Client"),
            accessor: "cxHClient.name",
            Filter: inputFilter,
            Cell: (row) => {
                return WithTooltip(row.value);
            },
        },
        {
            Header: WithTooltip("Supplier"),
            accessor: "supplier.name",
            Filter: inputFilter,
            Cell: (row) => {
                return WithTooltip(row.value);
            },
        },
        {
            Header: WithTooltip("Shipment Type"),
            accessor: "shipmentType",
            Cell: (row) => {
                const type = SHIPMENT_TYPE_MAP[row.original.shipmentType].label;

                return WithTooltip(type);
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || "active"}
                        id="select-type-filter"
                        options={SHIPMENT_FILTER_TYPES}
                        excludeSelect={true}
                    />
                );
            },
        },
        {
            Header: WithTooltip("Status"),
            accessor: "shipmentProfilingStateId",
            Cell: (row) => {
                const state = statuses.find((status) => row.value === status.stateId);

                return WithTooltip(state?.text);
            },
            filterMethod: (filter, row) => {
                if (filter.value && filter.value !== "active" && statuses) {
                    const rowStatus = statuses.find((status) => row[filter.id] === status.value);

                    if (rowStatus.stateId === 1) {
                        rowStatus.state = "open";
                    }

                    return rowStatus.state === filter.value;
                } else {
                    return true;
                }
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || "active"}
                        id="select-status-filter"
                        options={statuses}
                        excludeSelect={true}
                    />
                );
            },
        },
        {
            Header: WithTooltip("Team Assignment"),
            accessor: "profilingTasksAssignmentGroupKey",
            filterable: true,
            Cell: (row) => {
                return row.original.profilingTasksAssignmentGroupName;
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => {
                            onChange(event.target.value);

                            return updateTeamFilter(event.target.value);
                        }}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || ""}
                        excludeSelect={true}
                        id="select-all-filter"
                        options={[...basicOptions, ...assignments]}
                    />
                );
            },
        },
        {
            Header: WithTooltip("User Assignment"),
            accessor: "profilingTasksAssignedUserInfo",
            id: "userId",
            filterable: true,
            Cell: (row) => {
                const displayName = row.value ? row.value.fullName : "";

                return <span>{displayName}</span>;
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        style={{ width: "100%" }}
                        value={(filter && filter.value) || ""}
                        excludeSelect={true}
                        id="select-all-filter"
                        options={[...basicOptions, ...userAssignments]}
                    />
                );
            },
        },
        {
            Header: WithIcon("Notes", faStickyNote),
            accessor: "note",
            sortable: false,
            Cell: (row) => {
                return (
                    <NoteButton
                        shipmentKey={row.original.key}
                        shipmentProductKey={row.original.shipmentProductKey}
                        noteType="Shipment"
                        cxHClientCode={row.original.cxHClientCode}
                        hasNotes={row.original.hasNotes}
                        sourcePage={SOURCE_PAGE}
                    />
                );
            },
            filterable: false,
            width: 40,
        },
    ];
};
